import { useQuery } from "@tanstack/react-query";
import {
  ActionButton,
  DataTable,
  EmptyState,
  GridEmptyRow,
  LinkButton,
  Tag,
  Toolbar,
  dataColumns,
  errorText,
  formatDate,
  type DataColumn,
} from "@amber/ui";
import { ACCESS_ACTIONS, type DomainRow } from "../contracts/index";
import { domainsQuery } from "./queries";
import { find } from "./actions";
import type { DialogState } from "./dialogs/AccessDialogs";

const col = dataColumns<DomainRow>();

function domainColumns(onDialog: (state: DialogState) => void): DataColumn<DomainRow>[] {
  return [
    col.display({
      id: "domain",
      header: "Domain",
      meta: { width: 220, stickyCell: true, cellClassName: "font-mono text-md text-fg" },
      cell: ({ row }) => row.original.domain,
    }),
    col.display({
      id: "role",
      header: "Default role",
      meta: { width: 120 },
      cell: () => <Tag tone="strong">Viewer</Tag>,
    }),
    col.display({
      id: "joined",
      header: "People joined",
      meta: { width: 120, cellClassName: "tabular" },
      cell: ({ row }) => row.original.people_joined,
    }),
    col.display({
      id: "added_by",
      header: "Added by",
      meta: { width: 170 },
      cell: ({ row }) => row.original.added_by,
    }),
    col.display({
      id: "date",
      header: "Date",
      meta: { width: 110 },
      cell: ({ row }) => formatDate(row.original.added_at),
    }),
    col.display({
      id: "actions",
      meta: { label: "Actions", width: 110, cellClassName: "overflow-visible" },
      cell: ({ row }) => (
        <ActionButton
          action={find(row.original.actions, ACCESS_ACTIONS.removeDomain)}
          label="Remove…"
          onClick={() => {
            onDialog({ kind: "removeDomain", domain: row.original });
          }}
        />
      ),
    }),
  ];
}

export function DomainsTable({ onDialog }: { onDialog: (state: DialogState) => void }) {
  const domains = useQuery(domainsQuery);
  const columns = 6;
  return (
    <>
      <Toolbar>
        <span className="text-md text-muted">
          Anyone who signs in with an address from one of these domains joins as a Viewer.
        </span>
      </Toolbar>
      <DataTable
        label="Auto-join domains"
        columns={domainColumns(onDialog)}
        rows={domains.data?.rows ?? []}
        rowId={(r) => r.id}
        minWidth={820}
        isPending={domains.isPending}
        skeletonRows={3}
        emptyRow={
          domains.isError ? (
            <GridEmptyRow columns={columns}>
              Couldn&apos;t load auto-join domains. {errorText(domains.error)}{" "}
              <LinkButton
                onClick={() => {
                  void domains.refetch();
                }}
              >
                Retry
              </LinkButton>
            </GridEmptyRow>
          ) : (
            <tr>
              <td colSpan={columns} className="border-b border-divider bg-bg">
                <EmptyState title="No auto-join domains">
                  Only invited people can sign in. Add a domain to let colleagues join as Viewers on their own.
                </EmptyState>
              </td>
            </tr>
          )
        }
      />
    </>
  );
}
